import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Form } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import { DATE_FORMATS } from '../../../../../common/constants';
import { formatDate } from '../../../../../common/utils';
import GoBackButton from '../../../../../components/GoBackButton';
import LoaderComponent from '../../../../../components/LoaderComponent';
import Portal from '../../../../../components/Portal';
import { UPDATE_USER } from '../../../graphql/Mutations';
import StaffForm from '../../sales/components/SalesTeamForm';
import ReplaceLibrarianModal from '../components/ReplaceLibrarianModal';
import { STAFF_USER } from '../graphql/Queries';
import {
  LIBRARY_ALREADY_HAS_LIBRARIAN,
  LIBRARY_STAFF_BASE_PATH,
  STAFF_ROLES,
  STAFF_ROLE_KEYS,
  getStaffRole,
} from '../staffConfig';

const EditStaff = (props) => {
  const { match: { params: { id } = {} } = {} } = props;

  const history = useHistory();
  const location = useLocation();
  const { returnPath = LIBRARY_STAFF_BASE_PATH } = location?.state || {};
  const [form] = Form.useForm();
  const { dispatch } = useContext(AppContext);
  const [staffDetail, setStaffDetail] = useState({});
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isStaffLoading, setIsStaffLoading] = useState(true);

  const [replacePrompt, setReplacePrompt] = useState(null);
  const [isReplaceLoading, setIsReplaceLoading] = useState(false);
  const lastPayloadRef = useRef(null);

  const [updateUser] = useMutation(UPDATE_USER, {
    context: { skipToastCodes: [LIBRARY_ALREADY_HAS_LIBRARIAN] },
    onError: (error) => {
      const graphQLError = error?.graphQLErrors?.[0];
      if (graphQLError?.extensions?.code === LIBRARY_ALREADY_HAS_LIBRARIAN) {
        setReplacePrompt({ message: graphQLError?.message });
      }
    },
  });

  const handleReplaceLibrarian = async () => {
    setIsReplaceLoading(true);
    const response = await updateUser({
      variables: {
        where: { id },
        data: { ...lastPayloadRef.current, replaceLibrarian: true },
      },
    });
    setIsReplaceLoading(false);
    if (response?.data) {
      setReplacePrompt(null);
      history?.replace(returnPath);
    }
  };

  const [fetchUser] = useLazyQuery(STAFF_USER, {
    onCompleted: (response) => {
      setStaffDetail(response?.user);
      setIsStaffLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setIsStaffLoading(false);
    },
  });

  useEffect(() => {
    setIsStaffLoading(true);
    fetchUser({ variables: { where: { id } } });
  }, []);

  const currentStaffRole =
    staffDetail?.libraryStaff?.role || getStaffRole(staffDetail?.roles);

  const updateStaffFun = async (staffDetails) => {
    const roles =
      staffDetails?.staffRole && staffDetails?.staffRole !== currentStaffRole
        ? [
            ...(staffDetail?.roles || []).filter(
              (role) => !STAFF_ROLE_KEYS.includes(role),
            ),
            staffDetails?.staffRole,
          ]
        : undefined;
    setIsSubmitLoading(true);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
    lastPayloadRef.current = {
      firstName: staffDetails?.firstName?.trim(),
      lastName: staffDetails?.lastName?.trim(),
      gender: staffDetails?.gender,
      city: staffDetails?.city,
      state: staffDetails?.state,
      contactNo: staffDetails?.contact?.trim(),
      address: staffDetails?.address?.trim(),
      address2: staffDetails?.address2?.trim(),
      locality: staffDetails?.locality?.trim(),
      landmark: staffDetails?.landmark?.trim(),
      email: staffDetails?.email?.trim(),
      pincode: staffDetails?.pincode,
      staffLibraryId: staffDetails?.staffLibraryId,
      roles,
    };
    const response = await updateUser({
      variables: { where: { id }, data: lastPayloadRef.current },
    });
    if (response?.data) {
      history?.replace(returnPath);
    }
    setIsSubmitLoading(false);
  };

  const handleShowPrompt = () => {
    setIsDisabled(false);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: true,
    });
  };

  const initialValues = {
    ...staffDetail,
    contact: staffDetail?.contactNo,
    pincode: staffDetail?.pincode?.pincode,
    staffLibraryId: staffDetail?.libraryStaff?.library?.id,
    staffRole: currentStaffRole,
  };

  const headerButtons = (
    <div className="header-btn-wrapper">
      <Button
        type="primary"
        className="common-button mr-16"
        size="middle"
        onClick={() => history?.push(returnPath)}
      >
        Discard Changes
      </Button>
      <Button
        className="common-button"
        size="middle"
        onClick={form.submit}
        loading={isSubmitLoading}
        disabled={isDisabled}
      >
        Save
      </Button>
    </div>
  );

  return (
    <>
      <ReplaceLibrarianModal
        open={!!replacePrompt}
        message={replacePrompt?.message}
        loading={isReplaceLoading}
        onConfirm={handleReplaceLibrarian}
        onCancel={() => setReplacePrompt(null)}
      />
      <Portal portalId="header-title-content">
        <GoBackButton customLink={returnPath} />
        <div className="portal-header">Edit Library Staff</div>
      </Portal>
      <Portal portalId="header-right-content">{headerButtons}</Portal>
      <Portal portalId="header-right-content-phones">{headerButtons}</Portal>
      <Card
        className="full-height-with-nav"
        title="Library Staff Details"
        extra={
          <strong>
            Onboarded On :{' '}
            {formatDate(staffDetail?.createdAt, DATE_FORMATS?.ONBOARD) || '-'}
          </strong>
        }
      >
        {isStaffLoading ? (
          <LoaderComponent size="large" setHeight="60" spinning />
        ) : (
          <div className="delivery-form">
            <StaffForm
              form={form}
              onFinish={updateStaffFun}
              handleShowPrompt={handleShowPrompt}
              initialValues={initialValues}
              deliveryDetail={staffDetail}
              showLibrary
              roleOptions={STAFF_ROLES}
              hideCountry
              libraryData={staffDetail?.libraryStaff?.library}
            />
          </div>
        )}
      </Card>
    </>
  );
};

export default EditStaff;
